import { cn } from "@/lib/utils";
import { useWidgetResize, ResizeDirection } from "@/hooks/useWidgetResize";
import { Button } from "@/components/ui/button";
import { X, GripVertical, Minimize2, Square, Maximize2, Move } from "lucide-react";
import { useState, useEffect } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ResizableWidgetProps {
  widgetId: string;
  isEditMode: boolean;
  onRemove: () => void;
  onResize?: (widgetId: string, width: number, height: number) => void;
  onSizePreset?: (widgetId: string, preset: "small" | "medium" | "large") => void;
  initialHeight?: number;
  initialWidth?: number;
  children: React.ReactNode;
}

const SIZE_PRESETS = [
  { id: "small" as const, label: "Small", icon: Minimize2, description: "1 column, compact height" },
  { id: "medium" as const, label: "Medium", icon: Square, description: "1 column, standard height" },
  { id: "large" as const, label: "Large", icon: Maximize2, description: "2 columns, expanded height" },
];

export function ResizableWidget({
  widgetId,
  isEditMode,
  onRemove,
  onResize,
  onSizePreset,
  initialHeight = 200,
  initialWidth = 300,
  children,
}: ResizableWidgetProps) {
  const [savedHeight, setSavedHeight] = useState(initialHeight);
  const [savedWidth, setSavedWidth] = useState(initialWidth);
  
  const { containerRef, isResizing, currentSize, handleResizeStart } = useWidgetResize({
    minWidth: 200,
    minHeight: 150,
    isEnabled: isEditMode,
    onResize: (width, height) => {
      setSavedHeight(height);
      setSavedWidth(width);
      onResize?.(widgetId, width, height);
    },
  });

  useEffect(() => {
    setSavedHeight(initialHeight);
  }, [initialHeight]);

  useEffect(() => {
    setSavedWidth(initialWidth);
  }, [initialWidth]);

  const displayHeight = isResizing ? currentSize.height : savedHeight;
  const displayWidth = isResizing ? currentSize.width : savedWidth;

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative rounded-lg transition-shadow",
        isEditMode && "ring-2 ring-primary/30 hover:ring-primary/50",
        isResizing && "ring-2 ring-primary shadow-xl z-50"
      )}
      style={{ 
        minHeight: `${displayHeight}px`,
        height: isResizing ? `${displayHeight}px` : 'auto',
      }}
    >
      {isEditMode && (
        <>
          {/* Drag handle */}
          <div className="drag-handle absolute top-2 left-2 z-20 cursor-grab active:cursor-grabbing p-1.5 rounded-md bg-background/95 border shadow-sm hover:bg-muted transition-colors">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          
          {/* Size presets */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-0.5 bg-background/95 backdrop-blur-sm rounded-lg border shadow-sm p-0.5">
            <TooltipProvider delayDuration={300}>
              {SIZE_PRESETS.map((preset) => (
                <Tooltip key={preset.id}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 hover:bg-primary/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSizePreset?.(widgetId, preset.id);
                      }}
                    >
                      <preset.icon className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    <p className="font-medium">{preset.label}</p>
                    <p className="text-muted-foreground">{preset.description}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </TooltipProvider>
          </div>
          
          {/* Remove button */}
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 z-20 h-7 w-7"
            onClick={onRemove}
          >
            <X className="h-3.5 w-3.5" />
          </Button>

          {/* Edge resize handles - more visible */}
          {/* Top edge */}
          <div
            className="absolute top-0 left-8 right-8 h-3 -translate-y-1/2 cursor-ns-resize group z-30"
            onMouseDown={(e) => handleResizeStart(e, "n")}
          >
            <div className={cn(
              "absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full transition-all",
              "bg-primary/40 group-hover:bg-primary group-hover:h-1.5",
              isResizing && "bg-primary h-1.5"
            )} />
          </div>
          
          {/* Bottom edge */}
          <div
            className="absolute bottom-0 left-8 right-8 h-3 translate-y-1/2 cursor-ns-resize group z-30"
            onMouseDown={(e) => handleResizeStart(e, "s")}
          >
            <div className={cn(
              "absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full transition-all",
              "bg-primary/40 group-hover:bg-primary group-hover:h-1.5",
              isResizing && "bg-primary h-1.5"
            )} />
          </div>
          
          {/* Left edge */}
          <div
            className="absolute left-0 top-8 bottom-8 w-3 -translate-x-1/2 cursor-ew-resize group z-30"
            onMouseDown={(e) => handleResizeStart(e, "w")}
          >
            <div className={cn(
              "absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 rounded-full transition-all",
              "bg-primary/40 group-hover:bg-primary group-hover:w-1.5",
              isResizing && "bg-primary w-1.5"
            )} />
          </div>
          
          {/* Right edge */}
          <div
            className="absolute right-0 top-8 bottom-8 w-3 translate-x-1/2 cursor-ew-resize group z-30"
            onMouseDown={(e) => handleResizeStart(e, "e")}
          >
            <div className={cn(
              "absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 rounded-full transition-all",
              "bg-primary/40 group-hover:bg-primary group-hover:w-1.5",
              isResizing && "bg-primary w-1.5"
            )} />
          </div>

          {/* Corner resize handles - highly visible */}
          {/* Top-left corner */}
          <div
            className="absolute top-0 left-0 w-5 h-5 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize z-40 group"
            onMouseDown={(e) => handleResizeStart(e, "nw")}
          >
            <div className={cn(
              "absolute inset-0.5 rounded-full border-2 bg-background transition-all",
              "border-primary/50 group-hover:border-primary group-hover:scale-110",
              isResizing && "border-primary scale-110"
            )} />
          </div>
          
          {/* Top-right corner */}
          <div
            className="absolute top-0 right-0 w-5 h-5 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize z-40 group"
            onMouseDown={(e) => handleResizeStart(e, "ne")}
          >
            <div className={cn(
              "absolute inset-0.5 rounded-full border-2 bg-background transition-all",
              "border-primary/50 group-hover:border-primary group-hover:scale-110",
              isResizing && "border-primary scale-110"
            )} />
          </div>
          
          {/* Bottom-left corner */}
          <div
            className="absolute bottom-0 left-0 w-5 h-5 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize z-40 group"
            onMouseDown={(e) => handleResizeStart(e, "sw")}
          >
            <div className={cn(
              "absolute inset-0.5 rounded-full border-2 bg-background transition-all",
              "border-primary/50 group-hover:border-primary group-hover:scale-110",
              isResizing && "border-primary scale-110"
            )} />
          </div>
          
          {/* Bottom-right corner - main resize handle */}
          <div
            className="absolute bottom-0 right-0 w-6 h-6 translate-x-1/2 translate-y-1/2 cursor-nwse-resize z-40 group"
            onMouseDown={(e) => handleResizeStart(e, "se")}
          >
            <div className={cn(
              "absolute inset-0 rounded-full border-2 bg-background flex items-center justify-center transition-all shadow-sm",
              "border-primary/60 group-hover:border-primary group-hover:scale-110 group-hover:shadow-md",
              isResizing && "border-primary scale-110 shadow-md"
            )}>
              <Move className="h-3 w-3 text-primary/70 group-hover:text-primary" />
            </div>
          </div>

          {/* Size indicator during resize */}
          {isResizing && (
            <div className="absolute bottom-8 right-8 z-50 px-3 py-1.5 bg-primary text-primary-foreground text-sm rounded-md font-mono shadow-lg">
              {Math.round(displayWidth)} × {Math.round(displayHeight)}
            </div>
          )}

          {/* Edit mode border */}
          <div className="absolute inset-0 pointer-events-none border-2 border-dashed border-primary/20 rounded-lg" />
        </>
      )}

      <div className={cn("h-full", isEditMode && "opacity-95")}>
        {children}
      </div>
    </div>
  );
}
